<template>
    <div class="bg-white p-6 rounded-lg shadow-lg">
        <h3 class="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span class="mr-2">🕐</span> Class Timing Distribution
        </h3>

        <!-- Summary Cards -->
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div class="bg-blue-50 p-3 rounded-lg">
                <p class="text-xs text-blue-600 font-medium">Peak Hours</p>
                <p class="text-lg font-bold text-blue-800">{{ getPeakPercentage() }}%</p>
            </div>
            <div class="bg-green-50 p-3 rounded-lg">
                <p class="text-xs text-green-600 font-medium">Morning</p>
                <p class="text-lg font-bold text-green-800">{{ getMorningPercentage() }}%</p>
            </div>
            <div class="bg-yellow-50 p-3 rounded-lg">
                <p class="text-xs text-yellow-600 font-medium">Afternoon</p>
                <p class="text-lg font-bold text-yellow-800">{{ getAfternoonPercentage() }}%</p>
            </div>
            <div class="bg-purple-50 p-3 rounded-lg">
                <p class="text-xs text-purple-600 font-medium">Evening</p>
                <p class="text-lg font-bold text-purple-800">{{ getEveningPercentage() }}%</p>
            </div>
        </div>

        <!-- Charts Container -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Peak vs Non-Peak Chart -->
            <div class="">
                <h4 class="text-lg font-semibold text-gray-700 mb-2 flex items-center">
                    <span class="mr-2">⏰</span> Peak vs Non-Peak Hours
                </h4>
                <div v-if="hasPeakVsNonPeakData">
                    <apexchart type="donut" height="300" :options="peakVsNonPeakOptions"
                        :series="peakVsNonPeakSeries" />
                </div>
                <div v-else class="text-gray-500 text-center py-16">
                    <div class="text-4xl mb-2">⏰</div>
                    <div>There is no data to show</div>
                </div>
            </div>

            <!-- Time of Day Chart -->
            <div class="">
                <h4 class="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                    <font-awesome-icon :icon="['fas', 'clock']" class="text-blue-600 mr-2" />
                    Time of Day Distribution
                </h4>
                <div v-if="hasTimeOfDayData">
                    <apexchart type="pie" height="300" :options="timeOfDayOptions" :series="timeOfDaySeries" />
                </div>
                <div v-else class="text-gray-500 text-center py-16">
                    <div class="text-4xl mb-2">🕐</div>
                    <div>There is no data to show</div>
                </div>
            </div>
        </div>

        <!-- Hourly Distribution Chart -->
        <div class="mt-6">
            <h4 class="text-md font-medium text-gray-700 mb-3">Hourly Class Distribution (6AM - 10PM)</h4>
            <div class="h-80">
                <apexchart type="bar" height="100%" :options="hourlyOptions" :series="hourlySeries" />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "ClassTimingDistributionChart",
    props: {
        timingData: {
            type: Object,
            required: true
        },
        powerBiColors: {
            type: Array,
            required: true
        }
    },
    computed: {
        hasPeakVsNonPeakData() {
            return this.getPeakVsNonPeakSeries().length > 0 && this.getPeakVsNonPeakSeries().some(count => count > 0);
        },
        hasTimeOfDayData() {
            return this.getTimeOfDaySeries().length > 0 && this.getTimeOfDaySeries().some(count => count > 0);
        },
        peakVsNonPeakOptions() {
            const labels = this.getPeakVsNonPeakLabels();
            const counts = this.getPeakVsNonPeakSeries();

            // Create labels with counts (like ProgramDistributionChart)
            const labelsWithCounts = labels.map((label, index) => `${label} (${counts[index]})`);

            return {
                labels: labelsWithCounts,
                legend: {
                    position: 'bottom',
                    fontSize: '12px',
                    fontWeight: 500
                },
                colors: this.powerBiColors.slice(0, labels.length),
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + '%';
                    }
                },
            };
        },
        peakVsNonPeakSeries() {
            return this.getPeakVsNonPeakSeries();
        },
        timeOfDayOptions() {
            const labels = this.getTimeOfDayLabels();
            const colors = this.getTimeOfDayColors(labels);

            return {
                labels: labels,
                colors: colors,
                legend: {
                    position: 'bottom',
                    formatter: (seriesName, opts) => {
                        const seriesIndex = opts.seriesIndex;
                        const value = this.timeOfDaySeries[seriesIndex];
                        return `${seriesName} - ${value}`;
                    },
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + '%';
                    },
                },
                tooltip: {
                    y: {
                        formatter: (val) => `${val} Classes`,
                    },
                },
                chart: {
                    type: 'pie',
                }
            };
        },
        timeOfDaySeries() {
            return this.getTimeOfDaySeries();
        },
        hourlyOptions() {
            const labels = this.getHourlyLabels();

            return {
                chart: {
                    type: 'bar',
                    toolbar: {
                        show: false
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '55%',
                        endingShape: 'rounded',
                        distributed: false
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                },
                xaxis: {
                    categories: labels,
                    labels: {
                        style: {
                            fontSize: '11px'
                        },
                        rotate: -45
                    },
                    title: {
                        text: 'Time of Day'
                    }
                },
                yaxis: {
                    title: {
                        text: 'Number of Classes'
                    }
                },
                fill: {
                    opacity: 1,
                    colors: [this.powerBiColors[5]]
                },
                tooltip: {
                    y: {
                        formatter: function (val) {
                            return val + " classes"
                        }
                    }
                },
                colors: [this.powerBiColors[5]]
            };
        },
        hourlySeries() {
            return [{
                name: 'Classes',
                data: this.getHourlySeries()
            }];
        }
    },
    methods: {
        getPeakPercentage() {
            if (this.timingData && this.timingData.summary && this.timingData.summary.peak_percentage !== undefined) {
                return this.timingData.summary.peak_percentage;
            }
            return 0;
        },
        getMorningPercentage() {
            if (this.timingData && this.timingData.summary && this.timingData.summary.morning_percentage !== undefined) {
                return this.timingData.summary.morning_percentage;
            }
            return 0;
        },
        getAfternoonPercentage() {
            if (this.timingData && this.timingData.summary && this.timingData.summary.afternoon_percentage !== undefined) {
                return this.timingData.summary.afternoon_percentage;
            }
            return 0;
        },
        getEveningPercentage() {
            if (this.timingData && this.timingData.summary && this.timingData.summary.evening_percentage !== undefined) {
                return this.timingData.summary.evening_percentage;
            }
            return 0;
        },
        getPeakVsNonPeakLabels() {
            if (this.timingData && this.timingData.peak_vs_non_peak && Array.isArray(this.timingData.peak_vs_non_peak)) {
                return this.timingData.peak_vs_non_peak.map(item => item.category);
            }
            return [];
        },
        getPeakVsNonPeakSeries() {
            if (this.timingData && this.timingData.peak_vs_non_peak && Array.isArray(this.timingData.peak_vs_non_peak)) {
                return this.timingData.peak_vs_non_peak.map(item => item.class_count);
            }
            return [];
        },
        getTimeOfDayLabels() {
            if (this.timingData && this.timingData.time_of_day && Array.isArray(this.timingData.time_of_day)) {
                return this.timingData.time_of_day.map(item => item.category);
            }
            return [];
        },
        getTimeOfDaySeries() {
            if (this.timingData && this.timingData.time_of_day && Array.isArray(this.timingData.time_of_day)) {
                return this.timingData.time_of_day.map(item => item.class_count);
            }
            return [];
        },
        getHourlyLabels() {
            if (this.timingData && this.timingData.hourly_distribution && Array.isArray(this.timingData.hourly_distribution)) {
                return this.timingData.hourly_distribution.map(item => item.label);
            }
            return [];
        },
        getHourlySeries() {
            if (this.timingData && this.timingData.hourly_distribution && Array.isArray(this.timingData.hourly_distribution)) {
                return this.timingData.hourly_distribution.map(item => item.class_count);
            }
            return [];
        },
        getTimeOfDayColors(labels) {
            const colorMap = {
                'Morning (6AM-11AM)': '#3498DB',
                'Afternoon (12PM-5PM)': '#E74C3C',
                'Evening (6PM-10PM)': '#F39C12'
            };

            return labels.map(label => colorMap[label] || '#95A5A6');
        }
    }
};
</script>

<style scoped>
/* Custom styles for better visual appeal */
.apexcharts-legend {
    padding-top: 20px;
}
</style>
